type ErrorSource = "window.error" | "unhandledrejection" | "react-boundary" | "fetch" | "xhr"

type RequestContext = {
  method?: string
  path?: string
  status?: number
  requestId?: string
}

type ReportContext = {
  source: ErrorSource
  componentStack?: string
  digest?: string
  request?: RequestContext
}

type Breadcrumb = {
  time: string
  type: "fetch" | "xhr"
  method: string
  path: string
  status: number
}

type WirePayload = {
  occurredAt: string
  release: string
  source: ErrorSource
  error: { name: string; message: string; stack: string; componentStack: string; digest: string }
  page: { path: string }
  request: RequestContext
  client: { language: string; viewport: string }
  breadcrumbs: Breadcrumb[]
}

const breadcrumbs: Breadcrumb[] = []
const recentReports = new Map<string, number>()
let installed = false
let nativeFetch: typeof window.fetch | undefined
let sentThisSession = 0
let networkSentThisSession = 0
let apiBlockSignalAt = Number.NEGATIVE_INFINITY

let pageLeftForeground = false

const MAX_REPORTS_PER_SESSION = 30
// 네트워크 실패(status 0)는 단말 회선 상태에 좌우돼 한 세션에서 연달아 터진다.
// 인앱 브라우저·모바일 네트워크 전환에서 특히 잦은데, 이게 전체 예산을 태우면 정작
// 같은 세션 뒤쪽의 애플리케이션 오류가 실려 나가지 못한다. 그래서 이 계열에만 상한을 둔다.
// 상한을 넘긴 세션은 회선 자체가 불안정한 세션이고, 같은 실패를 더 실어 보내도
// 새로 알 수 있는 게 없다. 나머지 예산은 애플리케이션 오류 몫으로 남는다.
const MAX_NETWORK_REPORTS_PER_SESSION = 5
const MAX_PAYLOAD_BYTES = 28 * 1024
const FIRST_PARTY_BASE_DOMAINS = ["revieworks.com", "reviewed.co.kr", "lawkit.kr", "medilawyer.co.kr"]
// 이 route는 앱 코드상 200(인증 여부) 또는 503(인증 게이트웨이 장애)만 반환한다.
// 따라서 브라우저가 여기서 401/403을 받는 일은 앱 안에서는 일어날 수 없다.
const APP_ONLY_AUTH_PROBE_PATH = "/api/auth/status"
// 차단 신호의 유효기간.
//
// AuthProvider 는 마운트와 포커스 복귀 때만 이 경로를 찌른다. SPA 내비게이션 중에는
// 재마운트가 없으므로, 창을 짧게 잡으면 **차단이 계속되는 동안에도** 만료돼 노이즈가
// 다시 새어 나온다(한 세션의 리포트 예산 30건을 그대로 태운다).
// 반대로 무기한 유지하면 차단이 조용히 풀린 뒤의 실제 회귀를 영영 가린다.
//
// 그래서 한 번의 브라우징 세션을 덮을 만큼 길게 잡되(10분) 만료는 반드시 시킨다.
// 침묵 범위 자체가 이미 좁다 — 상태를 바꾸지 않는 요청의 401/403 뿐이고,
// `/api/*` 성공 응답 한 건이면 즉시 해제된다.
const API_BLOCK_SIGNAL_TTL_MS = 600_000

const release = process.env.NEXT_PUBLIC_RELEASE || "unknown"
const explicitEndpoint = process.env.NEXT_PUBLIC_CLIENT_ERROR_ENDPOINT || ""

function endpointFor(hostname: string): string {
  if (explicitEndpoint) return explicitEndpoint
  const host = hostname.toLowerCase()
  if (host === "localhost" || host === "127.0.0.1") return ""
  const gatewayLabel = host.split(".").some((label) => ["dev", "stage", "staging", "imsi", "next"].includes(label)) ? "gateway.dev" : "gateway"
  if (host === "contents.revieworks.com" || host.endsWith(".contents.revieworks.com")) {
    return `https://${gatewayLabel}.revieworks.com/client-errors`
  }
  if (host === "revieworks.com" || host.endsWith(".revieworks.com")) {
    return `https://${gatewayLabel}.revieworks.com/client-errors`
  }
  if (host === "reviewed.co.kr" || host.endsWith(".reviewed.co.kr")) {
    return `https://${gatewayLabel}.reviewed.co.kr/client-errors`
  }
  if (host === "lawkit.kr" || host.endsWith(".lawkit.kr")) {
    return `https://${gatewayLabel}.lawkit.kr/client-errors`
  }
  if (host === "medilawyer.co.kr" || host.endsWith(".medilawyer.co.kr")) {
    return `https://${gatewayLabel}.medilawyer.co.kr/client-errors`
  }
  return ""
}

function pathOnly(value: string | URL): string {
  try {
    return new URL(String(value), window.location.origin).pathname || "/"
  } catch {
    return String(value).split(/[?#]/, 1)[0].slice(0, 500)
  }
}

function isFirstPartyRequest(value: string | URL): boolean {
  try {
    const url = new URL(String(value), window.location.origin)
    if (url.origin === window.location.origin) return true
    const host = url.hostname.toLowerCase()
    return FIRST_PARTY_BASE_DOMAINS.some((base) => host === base || host.endsWith(`.${base}`))
  } catch {
    return false
  }
}

function isHttpError(status: number): boolean {
  return status >= 400 && status <= 599
}

function isCredentialRejection(status: number): boolean {
  return status === 401 || status === 403
}

function isOwnApiPath(path: string): boolean {
  return path === "/api" || path.startsWith("/api/")
}

/**
 * 자체 `/api/*`가 앱 앞단에서 통째로 거절되는 세션인지 추적한다.
 * `/api/auth/status`의 401/403은 앱이 만들 수 없는 응답이므로(200/503만 반환) 요청이
 * Next에 닿기 전에 앞단(WAF·사내 프록시·보안 확장 등)이 막았다는 증거다.
 *
 * 해제 조건은 둘이다. 하나라도 걸리면 보고를 재개한다.
 * - `/api/*` 응답이 앱에 닿았다는 증거(2xx/3xx)를 한 건이라도 보면 **즉시** 해제
 * - 그런 증거가 끝내 없더라도 `API_BLOCK_SIGNAL_TTL_MS` 가 지나면 **스스로** 만료
 *   (신호를 무기한 붙들면, 차단이 조용히 풀린 뒤의 진짜 401/403 회귀를 영영 가린다)
 */
function observeOwnApiAccess(path: string, status: number): void {
  if (!isOwnApiPath(path)) return
  if (path === APP_ONLY_AUTH_PROBE_PATH && isCredentialRejection(status)) {
    apiBlockSignalAt = Date.now()
    return
  }
  if (status >= 200 && status < 400) apiBlockSignalAt = Number.NEGATIVE_INFINITY
}

// 화면 진입·조회만으로 자동 발행되거나, 메서드는 POST 지만 사용자 상태를 바꾸지 않는 요청.
// 검색 실행·랭킹 조회는 본문에 조건을 실어야 해서 POST 이고, 조회수 집계는 화면 진입 시
// 자동으로 나간다. 앞단 차단 세션에서 이들이 목록·상세마다 티켓을 만들면 세션 예산(30건)을
// 태워, 정작 차단이 풀린 뒤의 실제 장애를 싣지 못한다.
//
// 로그인·회원가입·문의·즐겨찾기·도움돼요 같은 **실제 뮤테이션은 여기에 없다.**
// 그것들의 401/403 은 차단 중에도 그대로 보고한다(2026-08-05~11 실사고 유형).
const NON_MUTATING_POST_SUFFIXES = [
  "/search/apis/search/unrestricted",
  "/search/apis/search/unrestricted/rankings",
]
const VIEW_COUNTER_PATH = /\/unrestricted\/detail\/\d+\/view$/

function isNonMutatingRequest(method: string, path: string): boolean {
  if (method === "GET") return true
  if (method !== "POST") return false
  if (VIEW_COUNTER_PATH.test(path)) return true
  return NON_MUTATING_POST_SUFFIXES.some((suffix) => path.endsWith(suffix))
}

/**
 * 브라우저·플랫폼이 스스로 훑는 표준 경로와 정적 자원. 우리가 제공하지 않으면 404 가
 * <b>올바른 응답</b>이라 앱 오류로 볼 수 없다.
 *
 * 실제로 한 세션이 600ms 안에 `.well-known` 을 일괄 조회해 오류 티켓 여러 건을 만들었다
 * (gpc.json · privacy-sandbox-attestations.json · security.txt · apple-app-site-association ·
 *  web-identity · passkey-endpoints · webauthn · email-verification · change-password).
 * 그중 `resource-that-should-not-exist-whose-status-code-should-not-be-200/` 는 W3C
 * change-password 명세의 soft-404 판별 프로브라 **404 가 나와야 정상**이고 200 이면 결함이다.
 *
 * 기준은 백엔드 `ControllerExceptionAdvice.isNotifiable404` 과 같게 맞춘다 — 앞단·뒷단이
 * 같은 경로를 두고 서로 다르게 판단하면 한쪽만 알림이 울어 원인 추적이 어긋난다.
 */
const BROWSER_PROBE_PREFIXES = ["/.well-known/", "/favicon", "/robots.txt", "/sitemap", "/apple-touch-icon"]

function isBrowserProbePath(path: string): boolean {
  if (BROWSER_PROBE_PREFIXES.some((prefix) => path.startsWith(prefix))) return true
  // 빌드 산출물은 확장자 규칙에서 제외한다. `/_next/static/chunks/*.js` 의 404 는 구버전
  // HTML 이 삭제된 청크를 가리킨다는 뜻이라 화면이 실제로 깨지는 **배포 사고 신호**다.
  // (백엔드는 JS 청크를 서빙하지 않아 같은 규칙을 써도 이 위험이 없다.)
  if (path.startsWith("/_next/")) return false
  return path.slice(path.lastIndexOf("/") + 1).includes(".")
}

/**
 * 앞단 차단은 앱이 고칠 수 없는데도 요청 경로 수만큼 별개의 오류 티켓을 만들고
 * 세션당 리포트 예산(MAX_REPORTS_PER_SESSION)까지 소진한다. 차단이 증명된 세션에서는
 * 자체 `/api/*` **GET** 의 401/403을 breadcrumb으로만 남긴다.
 *
 * **뮤테이션(GET 이외)은 차단 중에도 반드시 보고한다.** 2026-08-05~11 실사고가 바로
 * 프록시 same-origin 가드 오작동으로 정상 사용자 전원의 뮤테이션이 403 이 된 건이었다.
 * 로그인 직전 status 프로브 하나가 일시적으로 403 을 받았다는 이유로 그 사고를
 * 침묵시켜서는 안 된다.
 *
 * 5xx와 그 밖의 4xx, `/api` 밖 경로, 그리고 차단 신호가 없거나 만료된 세션의
 * 401/403 도 그대로 보고한다.
 */
function isReportableHttpStatus(method: string, path: string, status: number): boolean {
  if (!isHttpError(status)) return false
  // 404 에만 적용한다. 같은 경로의 5xx 는 서버가 답을 못 준 것이므로 그대로 보고한다.
  if (status === 404 && isBrowserProbePath(path)) return false
  const sinceBlockSignal = Date.now() - apiBlockSignalAt
  const suppressed =
    isNonMutatingRequest(method, path) &&
    isOwnApiPath(path) &&
    isCredentialRejection(status) &&
    // 시스템 시계가 뒤로 뛰면 경과가 음수가 된다. 그때는 침묵을 연장하지 않고 보고한다.
    sinceBlockSignal >= 0 &&
    sinceBlockSignal <= API_BLOCK_SIGNAL_TTL_MS
  return !suppressed
}

// 검색엔진 렌더러·헤드리스 브라우저·스크래퍼는 우리가 고칠 수 없는 자기 정책으로 요청을
// 거절한다. robots.txt가 막아둔 `/api/*`가 대표적이라 병원 상세 같은 공개 페이지에서
// auth status·proxy 호출이 통째로 4xx가 되지만, 이는 애플리케이션 결함이 아니다.
// 실제 사용자 세션의 4xx/5xx는 그대로 수집하고, 자동화 세션만 계측에서 제외한다.
// `bot` 뒤에 구분자를 요구한다. 맨 `bot` 는 실사용자 단말명(`CUBOT NOTE 20`)을 잡고,
// `daum\/` 는 다음 앱 안드로이드 UA(`... Daum/4.9.5`)를 잡는다 — 둘 다 실제 사용자다.
// 자동화로 잘못 판정되면 그 세션은 오류가 통째로 침묵하므로 정밀도를 우선한다.
// 실제 크롤러는 `Googlebot/2.1` `bingbot/2.0` `AhrefsBot/7.0` `Applebot/0.1)` 처럼
// 이름 뒤에 `/` `;` `)` 가 붙고, 다음 크롤러는 `Daumoa` 라는 별도 이름을 쓴다.
const AUTOMATED_CLIENT_PATTERN = /bot[/;)]|crawler|crawling|spider|slurp|yeti|daumoa|headless|lighthouse|pagespeed|facebookexternalhit|embedly|scrapy|python-requests|curl\/|wget/i

function isAutomatedClient(): boolean {
  try {
    if (navigator.webdriver === true) return true
    return AUTOMATED_CLIENT_PATTERN.test(navigator.userAgent || "")
  } catch {
    return false
  }
}

// 페이지 이탈(pagehide)·백그라운드 전환(visibilitychange) 구간에서는 브라우저가
// 진행 중인 요청을 스스로 끊는다. 이때 남는 status 0은 서버 응답이 아니라 취소이며,
// fetch monitor가 AbortError를 제외하는 것과 같은 이유로 앱 오류로 볼 수 없다.
function isBrowserCancelledRequest(status: number): boolean {
  if (status !== 0) return false
  try {
    // 회선이 끊긴 단말에서 실패한 요청은 앱 결함이 아니다. 지하철·엘리베이터나
    // 인앱 브라우저의 네트워크 전환에서 흔하며, 페이지는 여전히 foreground 라서
    // 아래 lifecycle 조건만으로는 걸러지지 않는다.
    if (navigator.onLine === false) return true
    return pageLeftForeground || document.visibilityState === "hidden"
  } catch {
    return false
  }
}

// 인증 프로브의 네트워크 실패(status 0)는 보고 가치가 없다.
//
// 이 경로는 앱이 실패를 설계상 처리한다(AuthProvider 가 catch 해서 화면을 그대로 둔다).
// 게다가 마운트와 포커스 복귀마다 무조건 나가는 유일한 요청이라, 페이지가 파기되는
// 순간 in-flight 로 걸릴 확률이 어떤 요청보다 높다. 같은 탭 내 이동에서는 브라우저가
// AbortError 가 아니라 TypeError 를 던지고 visibilityState 도 아직 "visible" 이라,
// isBrowserCancelledRequest 로는 취소로 분류되지 않는다.
//
// 침묵 범위는 status 0(요청이 완결되지 못함)뿐이다. 이 경로의 실제 서버 문제는 503으로
// 오고, 그건 isReportableHttpStatus 가 계속 잡는다.
function isSilentNetworkFailure(path: string, status: number): boolean {
  return status === 0 && path === APP_ONLY_AUTH_PROBE_PATH
}

function installPageLifecycleMonitor(): void {
  window.addEventListener("pagehide", () => {
    pageLeftForeground = true
  })
  // bfcache 복귀·재활성화는 이탈이 아니므로 다시 수집한다.
  window.addEventListener("pageshow", () => {
    pageLeftForeground = false
  })
  document.addEventListener("visibilitychange", () => {
    pageLeftForeground = document.visibilityState === "hidden"
  })
}

function safeErrorText(value: string, limit: number): string {
  const withoutUrlQueries = value.replace(/https?:\/\/[^\s)\]}]+/g, (candidate) => {
    try {
      const url = new URL(candidate)
      url.search = ""
      url.hash = ""
      return url.toString()
    } catch {
      return candidate.split(/[?#]/, 1)[0]
    }
  })
  const withoutRelativeUrlQueries = withoutUrlQueries.replace(/((?:\.{0,2}\/)[^\s?#)\]}]+)[?#][^\s)\]}]*/g, "$1")
  return withoutRelativeUrlQueries
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, "[REDACTED_EMAIL]")
    .replace(/(?:\+?82[- ]?)?0?1[016789][-. ]?\d{3,4}[-. ]?\d{4}/g, "[REDACTED_PHONE]")
    .replace(/\b(Bearer|Basic)\s+[A-Za-z0-9._~+/=-]{8,}/gi, "$1 [REDACTED_TOKEN]")
    .slice(0, limit)
}

function errorFrom(value: unknown): Error {
  if (value instanceof Error) return value
  if (typeof value === "string") return new Error(value.slice(0, 1_500))
  return new Error(`Unhandled rejection (${Object.prototype.toString.call(value)})`)
}

function isNoise(error: Error): boolean {
  return error.name === "AbortError" || /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/i.test(error.message) || /^(Script error\.?|Load failed)$/i.test(error.message)
}

function remember(type: Breadcrumb["type"], request: RequestContext): void {
  breadcrumbs.push({
    time: new Date().toISOString(),
    type,
    method: (request.method || "GET").slice(0, 12),
    path: pathOnly(request.path || "/"),
    status: request.status || 0,
  })
  if (breadcrumbs.length > 20) breadcrumbs.splice(0, breadcrumbs.length - 20)
}

function responseRequestId(headers: Headers): string {
  return headers.get("x-request-id") || headers.get("x-amzn-requestid") || headers.get("trace-id") || ""
}

function shouldSend(error: Error, context: ReportContext): boolean {
  if (sentThisSession >= MAX_REPORTS_PER_SESSION) return false
  const networkFailure = context.request?.status === 0
  if (networkFailure && networkSentThisSession >= MAX_NETWORK_REPORTS_PER_SESSION) return false
  const firstFrame = (error.stack || "").split("\n")[1] || ""
  const key = [context.source, error.name, error.message, firstFrame, context.request?.path, context.request?.status].join("|")
  const now = Date.now()
  const previous = recentReports.get(key) || 0
  recentReports.set(key, now)
  if (recentReports.size > 100) {
    recentReports.forEach((time, candidate) => {
      if (now - time > 60_000) recentReports.delete(candidate)
    })
    while (recentReports.size > 100) {
      const oldest = recentReports.keys().next().value
      if (oldest === undefined) break
      recentReports.delete(oldest)
    }
  }
  if (now - previous <= 5_000) return false
  sentThisSession += 1
  if (networkFailure) networkSentThisSession += 1
  return true
}

function serializeWithinLimit(payload: WirePayload): string {
  let body = JSON.stringify(payload)
  if (new Blob([body]).size <= MAX_PAYLOAD_BYTES) return body

  for (const [stack, componentStack, crumbCount] of [
    [6_000, 2_500, 10],
    [3_000, 1_200, 5],
    [1_000, 400, 2],
    [0, 0, 0],
  ] as const) {
    body = JSON.stringify({
      ...payload,
      error: {
        ...payload.error,
        message: stack === 0 ? payload.error.message.slice(0, 500) : payload.error.message,
        stack: payload.error.stack.slice(0, stack),
        componentStack: payload.error.componentStack.slice(0, componentStack),
      },
      breadcrumbs: crumbCount === 0 ? [] : payload.breadcrumbs.slice(-crumbCount),
    })
    if (new Blob([body]).size <= MAX_PAYLOAD_BYTES) return body
  }
  return ""
}

function send(payload: WirePayload): void {
  const endpoint = endpointFor(window.location.hostname)
  if (!endpoint) return
  const body = serializeWithinLimit(payload)
  if (!body) return
  try {
    if (navigator.sendBeacon?.(endpoint, body)) return
  } catch {
    // fetch keepalive fallback below
  }
  const sendFetch = nativeFetch || window.fetch.bind(window)
  try {
    void Promise.resolve(
      sendFetch(endpoint, {
        method: "POST",
        body,
        headers: { "Content-Type": "text/plain;charset=UTF-8" },
        keepalive: true,
        credentials: "omit",
        mode: "cors",
      })
    ).catch(() => undefined)
  } catch {
    // 수집 실패는 원래 사용자 흐름에 절대 전파하지 않는다.
  }
}

export function reportFrontendError(value: unknown, context: ReportContext): void {
  try {
    if (typeof window === "undefined" || isAutomatedClient()) return
    const error = errorFrom(value)
    if (isNoise(error) || !shouldSend(error, context)) return
    const digest = context.digest || (error as Error & { digest?: string }).digest || ""
    send({
      occurredAt: new Date().toISOString(),
      release,
      source: context.source,
      error: {
        name: error.name || "Error",
        message: safeErrorText(error.message || "No message provided.", 1_500),
        stack: safeErrorText(error.stack || "", 12_000),
        componentStack: safeErrorText(context.componentStack || "", 6_000),
        digest: safeErrorText(digest, 160),
      },
      page: { path: window.location.pathname },
      request: context.request || {},
      client: {
        language: navigator.language || "",
        viewport: `${window.innerWidth}x${window.innerHeight}`,
      },
      breadcrumbs: breadcrumbs.slice(-20),
    })
  } catch {
    // 오류 reporter 자체가 애플리케이션 오류가 되어서는 안 된다.
  }
}

function installFetchMonitor(): void {
  nativeFetch = window.fetch.bind(window)
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    let method = "GET"
    let path = ""
    let firstParty = false
    try {
      const requestUrl = input instanceof Request ? input.url : String(input)
      method = (init?.method || (input instanceof Request ? input.method : "GET")).toUpperCase()
      path = pathOnly(requestUrl)
      firstParty = isFirstPartyRequest(requestUrl)
    } catch {
      // 메타데이터 추출 실패와 무관하게 원래 fetch는 그대로 실행한다.
    }
    try {
      const response = await nativeFetch!(input, init)
      try {
        if (firstParty) {
          const request = { method, path, status: response.status, requestId: responseRequestId(response.headers) }
          remember("fetch", request)
          observeOwnApiAccess(path, response.status)
          if (isReportableHttpStatus(method, path, response.status)) {
            reportFrontendError(new Error(`${method} ${path} failed with HTTP ${response.status}`), {
              source: "fetch",
              request,
            })
          }
        }
      } catch {
        // 원래 fetch response를 보존한다.
      }
      return response
    } catch (failure) {
      if (firstParty && !(failure instanceof DOMException && failure.name === "AbortError")) {
        try {
          const request = { method, path, status: 0 }
          remember("fetch", request)
          if (!isBrowserCancelledRequest(request.status) && !isSilentNetworkFailure(request.path, request.status)) {
            reportFrontendError(failure, { source: "fetch", request })
          }
        } catch {
          // 원래 fetch rejection을 보존한다.
        }
      }
      throw failure
    }
  }
}

function installXhrMonitor(): void {
  type XhrMeta = { method: string; path: string; ending: "" | "abort" | "timeout" }
  const metadata = new WeakMap<XMLHttpRequest, XhrMeta>()
  const originalOpen = XMLHttpRequest.prototype.open
  const originalSend = XMLHttpRequest.prototype.send

  XMLHttpRequest.prototype.open = function monitoredOpen(method: string, url: string | URL, async: boolean = true, username?: string | null, password?: string | null): void {
    originalOpen.call(this, method, url, async, username, password)
    metadata.delete(this)
    try {
      if (isFirstPartyRequest(url)) {
        metadata.set(this, { method: method.toUpperCase(), path: pathOnly(url), ending: "" })
      }
    } catch {
      // 원래 XHR open 성공 여부에 영향을 주지 않는다.
    }
  }

  XMLHttpRequest.prototype.send = function monitoredSend(body?: Document | XMLHttpRequestBodyInit | null): void {
    const meta = metadata.get(this)
    if (meta) {
      try {
        this.addEventListener("abort", () => {
          meta.ending = "abort"
        })
        this.addEventListener("timeout", () => {
          meta.ending = "timeout"
        })
        this.addEventListener(
          "loadend",
          () => {
            try {
              const request = {
                method: meta.method,
                path: meta.path,
                status: this.status || 0,
                requestId: this.getResponseHeader("x-request-id") || this.getResponseHeader("x-amzn-requestid") || "",
              }
              remember("xhr", request)
              observeOwnApiAccess(meta.path, this.status)
              // `timeout` 은 브라우저가 끊은 게 아니라 서버가 제때 답하지 않은 것이다.
              // 백그라운드에서 30초 axios timeout 이 만료돼도 실제 장애이므로 보고한다.
              const cancelled =
                meta.ending === "abort" ||
                (meta.ending !== "timeout" && isBrowserCancelledRequest(request.status))
              const silent = isSilentNetworkFailure(meta.path, request.status)
              if (!cancelled && !silent && (this.status === 0 || isReportableHttpStatus(meta.method, meta.path, this.status))) {
                const failureKind = this.status || (meta.ending === "timeout" ? "timeout" : "network")
                reportFrontendError(new Error(`${meta.method} ${meta.path} failed with HTTP ${failureKind}`), {
                  source: "xhr",
                  request,
                })
              }
            } catch {
              // XHR loadend consumer에 reporter 오류를 노출하지 않는다.
            }
          },
          { once: true }
        )
      } catch {
        // listener 설치 실패와 무관하게 원래 XHR send는 그대로 실행한다.
      }
    }
    originalSend.call(this, body ?? null)
  }
}

export function installFrontendErrorReporter(): void {
  if (typeof window === "undefined" || installed) return
  installed = true
  // 자동화 세션에서는 fetch/XHR patching 자체를 하지 않는다(보고 대상이 없으므로 계측 비용도 0).
  if (isAutomatedClient()) return
  try {
    installPageLifecycleMonitor()
  } catch {
    // 선택적 계측을 설치할 수 없는 브라우저에서도 앱은 정상 동작해야 한다.
  }
  try {
    installFetchMonitor()
  } catch {
    // 선택적 계측을 설치할 수 없는 브라우저에서도 앱은 정상 동작해야 한다.
  }
  try {
    installXhrMonitor()
  } catch {
    // 선택적 계측을 설치할 수 없는 브라우저에서도 앱은 정상 동작해야 한다.
  }
  try {
    window.addEventListener("error", (event) => {
      if (event.error) reportFrontendError(event.error, { source: "window.error" })
    })
    window.addEventListener("unhandledrejection", (event) => {
      reportFrontendError(event.reason, { source: "unhandledrejection" })
    })
  } catch {
    // 선택적 계측을 설치할 수 없는 브라우저에서도 앱은 정상 동작해야 한다.
  }
}
